import { useMemo } from "react";
import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import type { ListeningStepComponentProps } from "./types";
import { parseInstructionHtml } from "../../ReadingAssessment/utils";
import { InstructionAudioButton } from "../../../../components/InstructionAudioButton";

export function InstructionsStep({
  assessment,
  isLoading,
  instructionType = "overall",
}: ListeningStepComponentProps) {
  const renderedInstructions = useMemo(() => {
    switch (instructionType) {
      case "overall":
        return parseInstructionHtml(assessment?.overallInstructions ?? "");
      case "yes_no":
        return parseInstructionHtml(assessment?.yesNoInstructions ?? "");
      case "student_select":
        return parseInstructionHtml(
          assessment?.studentSelectionInstructions ?? "",
        );
      default:
        return parseInstructionHtml(assessment?.overallInstructions ?? "");
    }
  }, [
    assessment?.overallInstructions,
    assessment?.yesNoInstructions,
    assessment?.studentSelectionInstructions,
    instructionType,
  ]);

  const resolvedImage = useMemo(() => {
    switch (instructionType) {
      case "overall":
        return assessment?.instructionImage ?? "";
      case "yes_no":
        return assessment?.yesNoInstructionImage ?? "";
      case "student_select":
        return assessment?.studentSelectionInstructionImage ?? "";
      default:
        return "";
    }
  }, [
    assessment?.instructionImage,
    assessment?.yesNoInstructionImage,
    assessment?.studentSelectionInstructionImage,
    instructionType,
  ]);

  const resolvedAudio = useMemo(() => {
    switch (instructionType) {
      case "yes_no":
        return assessment?.yesNoInstructionsAudio ?? "";
      case "student_select":
        return assessment?.studentSelectionInstructionsAudio ?? "";
      default:
        return assessment?.instructionsAudio ?? "";
    }
  }, [
    assessment?.instructionsAudio,
    assessment?.yesNoInstructionsAudio,
    assessment?.studentSelectionInstructionsAudio,
    instructionType,
  ]);

  if (isLoading) {
    return null;
  }

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      gap={3}
      sx={{ width: "100%", maxWidth: 1400, mx: "auto" }}
    >
      <Card
        sx={{
          flex: "60%",
          display: "flex",
          flexDirection: "column",
          height: { xs: "40vh", md: "50vh" },
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {resolvedImage ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              component="img"
              src={resolvedImage}
              alt="Listening test instructions"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
              onError={(event) => {
                const target = event.currentTarget as HTMLImageElement;
                target.style.display = "none";
              }}
            />
          </Box>
        ) : null}
      </Card>
      <Card
        sx={{
          flex: "40%",
          display: "flex",
          flexDirection: "column",
          height: { xs: "40vh", md: "50vh" },
          minHeight: 0,
          overflow: "hidden",
        }}
        variant={"filled"}
      >
        <CardContent sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          <Stack spacing={2} alignItems={"flex-start"} sx={{ minWidth: 0 }}>
            <Stack direction={"row"} alignItems={"center"} spacing={2}>
              <InstructionAudioButton
                audioUrl={resolvedAudio}
                label={`Play instructions audio`}
              />
              <Typography variant={"body1"} sx={{ fontWeight: 800 }}>
                Instructions:
              </Typography>
            </Stack>
            {renderedInstructions ? (
              <Typography
                component="div"
                variant={"body1"}
                sx={{ whiteSpace: "pre-line", lineHeight: 1.2, minWidth: 0 }}
              >
                {renderedInstructions}
              </Typography>
            ) : (
              <Typography>No instructions available.</Typography>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
